import React, { useState } from "react";
import { Box } from "@mui/material";

export const normalizeMime = (mime = "") =>
  mime.toString().toLowerCase().split(";")[0].trim();

export const isImageAttachment = (attachment) => {
  if (!attachment) return false;
  const mime = normalizeMime(attachment.mime || "");
  if (mime.startsWith("image/")) return true;
  return attachment.kind === "image";
};

export const isAudioAttachment = (attachment) => {
  if (!attachment) return false;
  const mime = normalizeMime(attachment.mime || "");
  return mime.startsWith("audio/");
};

export const describeAttachment = (attachment, index = 0) => {
  const fallbackBase =
    attachment?.filename || attachment?.name || `file-${index + 1}`;
  const trimmed = (fallbackBase || "").trim() || `file-${index + 1}`;
  const parts = trimmed.split(".");
  const hasExt = parts.length > 1;
  const extFromName = hasExt ? parts.pop() : "";
  const mimeExt = normalizeMime(attachment?.mime || "").split("/")[1] || "";
  const extension = (extFromName || mimeExt || "file").toLowerCase();
  const typeLabel = extension ? extension.toUpperCase() : "FILE";
  const baseName = hasExt ? parts.join(".") : trimmed;
  const displayName = hasExt ? trimmed : `${baseName}.${extension}`;
  return { typeLabel, displayName };
};

const AttachmentDownloadLink = ({ attachment, index, isDark }) => {
  const href = attachment?.url || attachment?.previewUrl;
  const [downloading, setDownloading] = useState(false);
  if (!href) return null;

  const { typeLabel, displayName } = describeAttachment(attachment, index);
  const colors = isDark
    ? { border: "#333", bg: "rgba(255,255,255,0.03)", text: "#f5f5f5" }
    : { border: "#e5e7eb", bg: "#f8fafc", text: "#111827" };
  const isAudio = isAudioAttachment(attachment);

  const handleClick = async (e) => {
    e.preventDefault();
    if (downloading) return;
    setDownloading(true);
    try {
      const resp = await fetch(href);
      if (!resp.ok) throw new Error("Download failed");
      const blob = await resp.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = displayName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download attachment error:", error);
    } finally {
      setDownloading(false);
    }
  };

  if (isAudio) {
    return (
      <Box
        sx={{
          border: `1px solid ${colors.border}`,
          background: colors.bg,
          color: colors.text,
          borderRadius: 12,
          px: 1.5,
          py: 1.1,
          display: "flex",
          flexDirection: "column",
          gap: 0.75,
        }}
      >
        <Box sx={{ fontSize: "0.82rem", fontWeight: 600 }}>{displayName}</Box>
        <audio controls src={href} style={{ width: "100%" }} />
        <Box
          component="button"
          onClick={handleClick}
          disabled={downloading}
          style={{
            alignSelf: "flex-start",
            border: "none",
            background: "transparent",
            color: colors.text,
            fontSize: "0.75rem",
            fontWeight: 600,
            padding: 0,
            cursor: downloading ? "default" : "pointer",
          }}
        >
          {downloading ? "Downloading..." : "Download audio"}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component="button"
      type="button"
      onClick={handleClick}
      sx={{
        display: "inline-flex",
        flexWrap: "wrap",
        gap: 0.5,
        alignItems: "center",
        borderRadius: 2,
        border: `1px solid ${colors.border}`,
        backgroundColor: colors.bg,
        color: colors.text,
        fontSize: "0.9rem",
        fontWeight: 600,
        p: 2,
        textDecoration: "none",
        cursor: downloading ? "not-allowed" : "pointer",
        opacity: downloading ? 0.7 : 1,
      }}
    >
      <span>
        {downloading ? "Downloading..." : `Download the ${typeLabel}:`}
      </span>
      {!downloading && <span style={{ fontWeight: 700 }}>{displayName}</span>}
    </Box>
  );
};

const MessageAttachments = ({
  imageAttachments,
  fileAttachments,
  isDark,
  onOpenLightbox,
}) => {
  const hasImages = imageAttachments.length > 0;
  const hasFiles = fileAttachments.length > 0;

  if (!hasImages && !hasFiles) return null;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 1, mt: 1.25 }}>
      {hasImages && (
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px" }}>
          {imageAttachments.map((att, i) => {
            const src = att.thumbUrl || att.url || att.previewUrl;
            if (!src) return null;
            return (
              <button
                key={att.id || `${src}-${i}`}
                onClick={() => onOpenLightbox(i)}
                style={{
                  display: "inline-block",
                  padding: 0,
                  border: "none",
                  background: "transparent",
                  cursor: "pointer",
                  lineHeight: 0,
                }}
                aria-label="View image attachment"
              >
                <img
                  src={src}
                  alt={att.name || att.filename || `attachment-${i + 1}`}
                  style={{
                    width: 84,
                    height: 84,
                    borderRadius: 10,
                    objectFit: "cover",
                    display: "block",
                  }}
                />
              </button>
            );
          })}
        </Box>
      )}

      {hasFiles && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
          {fileAttachments.map((att, idx) => (
            <AttachmentDownloadLink
              key={att.id || `${att.filename || "file"}-${idx}`}
              attachment={att}
              index={idx}
              isDark={isDark}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default MessageAttachments;
